import {
    BookRegular,
    BoxRegular,
    CartRegular,
    CrownRegular,
    DocumentTable24Regular,
    Food24Regular,
    Gift24Regular,
    MegaphoneRegular,
    OvenRegular,
    People24Regular,
    PersonClockRegular,
    ShieldRegular,
    TagPercentRegular,
    VehicleCarRegular
} from "@fluentui/react-icons";
import {ComponentType} from "react";

export interface CoachProduct {
    id: string;
    name: string;
    skills: string[];
    /** Icon shown for this product in the skills-forward board. */
    icon: ComponentType;
}

export interface Coach {
    id: string;
    name: string;
    accent: string;
    icon: ComponentType;
    products: CoachProduct[];
}

export const COACHES: Coach[] = [
    {
        id: "sell",
        name: "Sell Coach",
        accent: "#2fdc9f",
        icon: Food24Regular,
        products: [
            {
                id: "ordering",
                name: "Ordering",
                skills: [
                    "Peak demand forecasting",
                    "Channel mix (dine-in vs delivery)",
                    "Upsell attach rate",
                    "3rd party platform performance"
                ],
                icon: CartRegular
            },
            {
                id: "driveThru",
                name: "Drive-Thru",
                skills: [
                    "Speed of service vs threshold",
                    "Queue cascade detection",
                    "Daypart throughput",
                    "Car count and peak capacity"
                ],
                icon: VehicleCarRegular
            },
            {
                id: "fraud",
                name: "Fraud",
                skills: [
                    "Void and refund patterns",
                    "Discount abuse detection",
                    "Employee behavior anomalies",
                    "Comping trend analysis"
                ],
                icon: ShieldRegular
            }
        ]
    },
    {
        id: "operate",
        name: "Operate Coach",
        accent: "#ffad42",
        icon: People24Regular,
        products: [
            {
                id: "labor",
                name: "Labor",
                skills: [
                    "Overstaffed and understaffed",
                    "Weather and local events",
                    "Labor deployment",
                    "Labor mix (manager vs crew)"
                ],
                icon: PersonClockRegular
            },
            {
                id: "inventory",
                name: "Inventory",
                skills: [
                    "Variance and waste trending",
                    "Par level optimization",
                    "Vendor shortage alerts",
                    "Shrink detection"
                ],
                icon: BoxRegular
            },
            {
                id: "kitchen",
                name: "Kitchen",
                skills: [
                    "Production vs demand sync",
                    "Ticket time analysis",
                    "Waste and yield tracking",
                    "Line speed bottlenecks"
                ],
                icon: OvenRegular
            }
        ]
    },
    {
        id: "engage",
        name: "Engage Coach",
        accent: "#37d2f1",
        icon: Gift24Regular,
        products: [
            {
                id: "loyalty",
                name: "Loyalty",
                skills: [
                    "Member engagement scoring",
                    "Redemption pattern analysis",
                    "Churn risk detection",
                    "Tier migration tracking"
                ],
                icon: CrownRegular
            },
            {
                id: "campaign",
                name: "Campaign",
                skills: [
                    "Offer performance tracking",
                    "Audience segmentation",
                    "ROI and lift measurement",
                    "A/B test results"
                ],
                icon: MegaphoneRegular
            },
            {
                id: "offers",
                name: "Offers",
                skills: [
                    "Discount effectiveness",
                    "Menu item attach rates",
                    "Promo cannibalization",
                    "Redemption velocity"
                ],
                icon: TagPercentRegular
            }
        ]
    },
    {
        id: "restaurantCore",
        name: "Restaurant Core Coach",
        accent: "#9f86ff",
        icon: DocumentTable24Regular,
        products: [
            {
                id: "menu",
                name: "Menu",
                skills: [
                    "Item profitability ranking",
                    "Price elasticity signals",
                    "86'd item impact analysis",
                    "Menu engineering insights"
                ],
                icon: BookRegular
            }
        ]
    }
];

export const COACH_BY_ID: Record<string, Coach> = COACHES.reduce(
    (acc, coach) => {
        acc[coach.id] = coach;
        return acc;
    },
    {} as Record<string, Coach>
);

export const AGENT_TO_COACH: Record<string, string> = {
    agent_loyalty_iq: "engage",
    agent_campaign_coach: "engage",
    agent_guest_pulse: "engage",
    agent_pl_advisor: "operate",
    agent_ops_pulse: "sell",
    agent_loss_intel: "sell",
    agent_menu_core: "restaurantCore"
};

/**
 * Maps insight category IDs to the coach:product keys that gate them.
 * An action is available when ANY of the listed products is enabled.
 */
export const CATEGORY_TO_PRODUCTS: Record<string, string[]> = {
    punchh: ["engage:loyalty", "engage:campaign", "engage:offers"],
    labor: ["operate:labor"],
    cogs: ["operate:inventory", "operate:kitchen"],
    drivethru_sos: ["sell:driveThru"],
    menu: ["restaurantCore:menu"],
    sell: ["sell:fraud"],
    food_cost: ["operate:inventory"],
    loyalty_churn: ["engage:loyalty"],
    campaign_effectiveness: ["engage:campaign"]
};

/**
 * Maps InsightActionKind values to the coach:product keys required to use that action.
 * This is checked per-action so a schedule/labor action inside a non-labor card is still
 * gated by the Labor product even when the card's own category is enabled.
 */
export const KIND_TO_PRODUCTS: Record<string, string[]> = {
    schedule: ["operate:labor"],
    labor: ["operate:labor"],
    campaign: ["engage:campaign"],
    cogs: ["operate:inventory", "operate:kitchen"]
};

export const getTotalSkillsForCoach = (coach: Coach): number =>
    coach.products.length;
